import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useAuth } from "../../../../contexts/AuthContext";

function RulesEdit({ rule, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    model: "",
    model_name: "",
    field: "",
    type: "sum",
    leverage: 1,
    check_time: "",
    base_price: "",
  });
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [useCustomModel, setUseCustomModel] = useState(false);
  const { apiBaseUrl } = useAuth();

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (rule) {
      setFormData({
        model: rule.model || "",
        model_name: rule.model_name || "",
        field: rule.field || "",
        type: rule.type || "sum",
        leverage: parseFloat(rule.leverage) || 1,
        check_time:
          rule.check_time !== null && rule.check_time !== undefined
            ? rule.check_time
            : "",
        base_price: rule.base_price || "",
      });

      // Load conditions if exists
      if (rule.condition && typeof rule.condition === "object") {
        const conditionsArray = Object.entries(rule.condition).map(
          ([field, data]) => ({
            field: field,
            operator: data.operator || "=",
            value: data.value || "",
          })
        );
        setConditions(conditionsArray);
      } else {
        setConditions([]);
      }
    }
  }, [rule]);

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      const response = await axios.get(
        `${apiBaseUrl}/api/transfer-market/admin/models`
      );
      setModels(response.data.data || []);
      setLoadingModels(false);
    } catch (error) {
      console.error("❌ خطا در دریافت models:", error);
      setLoadingModels(false);
      toast.error("خطا در دریافت لیست مدل‌ها");
    }
  };

  const handleModelSelect = (e) => {
    const selectedModel = models.find(
      (m) => m.model_address === e.target.value
    );
    if (selectedModel) {
      setFormData({
        ...formData,
        model: selectedModel.model_address,
        model_name: selectedModel.name,
      });
    }
  };

  const addCondition = () => {
    setConditions([...conditions, { field: "", operator: "=", value: "" }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, key, value) => {
    const updated = [...conditions];
    updated[index][key] = value;
    setConditions(updated);
  };

  const parseValue = (value) => {
    if (value === "") return value;
    if (!isNaN(value) && value.trim() !== "") {
      return parseFloat(value);
    }
    return value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // ساخت شیء condition
      const conditionObj = {};
      conditions.forEach((cond) => {
        if (cond.field && cond.operator && cond.value !== "") {
          conditionObj[cond.field] = {
            operator: cond.operator,
            value: parseValue(cond.value),
          };
        }
      });

      const payload = {
        model: formData.model,
        model_name: formData.model_name,
        field: formData.field,
        type: formData.type,
        leverage: parseFloat(formData.leverage),
      };

      // فقط اگر مقدار داشتند اضافه کن
      if (formData.check_time !== "") {
        payload.check_time = parseInt(formData.check_time);
      }

      if (formData.base_price !== "") {
        payload.base_price = formData.base_price;
      }

      // فقط اگه condition داشتیم اضافه کن
      if (Object.keys(conditionObj).length > 0) {
        payload.condition = conditionObj;
      }

      const response = await axios.put(
        `${apiBaseUrl}/api/transfer-market/admin/rules/${rule.id}`,
        payload
      );
      toast.success("قانون با موفقیت ویرایش شد");
      setLoading(false);
      onSuccess();
    } catch (error) {
      console.error("❌ خطا در ویرایش قانون:", error);
      setLoading(false);
      toast.error("خطا در ویرایش قانون");
    }
  };

  if (!rule) {
    return (
      <div className="bg-gray-700/30 rounded-lg p-12 text-center border border-gray-600">
        <p className="text-gray-400">لطفاً یک قانون برای ویرایش انتخاب کنید</p>
        <button
          onClick={onCancel}
          className="mt-4 bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
        >
          بازگشت
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-600">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-yellow-600 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">
            ویرایش قانون #{rule.id}
          </h3>
          <p className="text-sm text-gray-400">اطلاعات قانون را ویرایش کنید</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Toggle between dropdown and manual input */}
        <div className="flex items-center gap-3 mb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useCustomModel}
              onChange={(e) => setUseCustomModel(e.target.checked)}
              className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
            />
            <span className="text-sm text-gray-300">ورود دستی مدل</span>
          </label>
        </div>

        {useCustomModel ? (
          <>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Model Address
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
                placeholder="مثال: App\\Models\\Player"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Model Name
              </label>
              <input
                type="text"
                value={formData.model_name}
                onChange={(e) =>
                  setFormData({ ...formData, model_name: e.target.value })
                }
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
                placeholder="مثال: Player Profile"
                required
              />
            </div>
          </>
        ) : (
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              مدل
            </label>
            {loadingModels ? (
              <div className="w-full bg-gray-700 text-gray-400 rounded-lg px-4 py-3 border border-gray-600 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                در حال بارگذاری مدل‌ها...
              </div>
            ) : (
              <>
                <select
                  value={formData.model}
                  onChange={handleModelSelect}
                  className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
                  required
                  disabled={models.length === 0}
                >
                  {models.length === 0 ? (
                    <option value="">هیچ مدلی یافت نشد</option>
                  ) : (
                    models.map((model) => (
                      <option key={model.id} value={model.model_address}>
                        {model.name}
                      </option>
                    ))
                  )}
                </select>
                {formData.model && (
                  <p className="text-xs text-gray-400 mt-1">
                    آدرس: {formData.model}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            فیلد
          </label>
          <input
            type="text"
            value={formData.field}
            onChange={(e) =>
              setFormData({ ...formData, field: e.target.value })
            }
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
            placeholder="مثال: value, updated_value, balance_after"
            required
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            نوع (Type)
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
            required
          >
            <option value="sum">Sum (جمع)</option>
            <option value="first">first (اولین)</option>
            <option value="last">last (اخرین)</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            زمان بررسی (Check Time)
            <span className="text-gray-500 text-xs mr-2">(اختیاری)</span>
          </label>
          <input
            type="number"
            min="0"
            max="23"
            step="1"
            value={formData.check_time}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setFormData({ ...formData, check_time: "" });
              } else {
                const numValue = parseInt(value);
                if (numValue >= 0 && numValue <= 23) {
                  setFormData({ ...formData, check_time: numValue });
                }
              }
            }}
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
            placeholder="مثال: 14 (برای ساعت 14:00)"
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            قیمت پایه (Base Price)
            <span className="text-gray-500 text-xs mr-2">(اختیاری)</span>
          </label>
          <input
            type="text"
            value={formData.base_price}
            onChange={(e) =>
              setFormData({ ...formData, base_price: e.target.value })
            }
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
            placeholder="مثال: 1000000, base_value, ..."
          />
        </div>

        {/* بخش Conditions */}
        <div className="border-t border-gray-600 pt-4">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-gray-300 text-sm font-medium">
              شرایط (Conditions)
            </label>
            <button
              type="button"
              onClick={addCondition}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              افزودن شرط
            </button>
          </div>

          {conditions.length === 0 ? (
            <div className="bg-gray-700/50 rounded-lg p-4 text-center text-gray-400 text-sm">
              هیچ شرطی اضافه نشده است.
            </div>
          ) : (
            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div
                  key={index}
                  className="bg-gray-700/50 rounded-lg p-4 border border-gray-600"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <label className="block text-gray-400 text-xs mb-1">
                        نام فیلد
                      </label>
                      <input
                        type="text"
                        value={condition.field}
                        onChange={(e) =>
                          updateCondition(index, "field", e.target.value)
                        }
                        className="w-full bg-gray-600 text-white rounded px-3 py-2 text-sm border border-gray-500 focus:border-blue-500 focus:outline-none"
                        placeholder="test_id, status, ..."
                        required
                      />
                    </div>

                    <div className="w-24">
                      <label className="block text-gray-400 text-xs mb-1">
                        عملگر
                      </label>
                      <select
                        value={condition.operator}
                        onChange={(e) =>
                          updateCondition(index, "operator", e.target.value)
                        }
                        className="w-full bg-gray-600 text-white rounded px-2 py-2 text-sm border border-gray-500 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="=">=</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                        <option value=">=">&gt;=</option>
                        <option value="<=">&lt;=</option>
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-gray-400 text-xs mb-1">
                        مقدار
                      </label>
                      <input
                        type="text"
                        value={condition.value}
                        onChange={(e) =>
                          updateCondition(index, "value", e.target.value)
                        }
                        className="w-full bg-gray-600 text-white rounded px-3 py-2 text-sm border border-gray-500 focus:border-blue-500 focus:outline-none"
                        placeholder="2, active, ..."
                        required
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeCondition(index)}
                      className="mt-5 bg-red-600 hover:bg-red-700 text-white p-2 rounded transition-colors"
                      title="حذف شرط"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            اهرم (Leverage)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.leverage}
            onChange={(e) =>
              setFormData({ ...formData, leverage: e.target.value })
            }
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
            placeholder="مثال: 10, 15.5"
            required
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                در حال ذخیره...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                ذخیره تغییرات
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300"
          >
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
}

export default RulesEdit;
